import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Grid,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Popover,
    TableSortLabel,
    Pagination
} from '@mui/material';
import { ActionsIcon, ArrowLeftIcon, ArrowRightIcon, DownArrow, Trash } from '../../../../assets';
import { fileUploadActions } from '../../../../redux/reducers/filesUploadReducer';
import { downloadFiles, getSortedData } from '../../../../utility';
import { CustomButton, TableRowsSkeleton } from '../../../shared';
import { deleteFiles, getFiles } from '../../../../redux/thunks/filesUploadThunk';
import ConfirmationDelete from '../confirmationDelete'

const FilesTable = () => {
    const dispatch = useDispatch();
    const { files, isSelectedAll, selectedFiles, isGetFilesLoading, currentPage, pageSize, totalPages,} = useSelector(state => state.filesUpload);
    const { company } = useSelector(state => state.company)
    const [anchorEl, setAnchorEl] = useState(null);
    const [openPopoverId, setOpenPopoverId] = useState(null);
    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState(null);
    const [openDelete, setOpenDelete] = useState(false);
    const Files_column = [
        { key: "file_name", value: "Name" },
        { key: "file_type", value: "Type" },
        { key: "created_at", value: "Uploaded Date" },
        { key: "options", value: "" },
    ];

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const createSortHandler = (property) => () => {
        handleRequestSort(property);
    };
    const sortedData = React.useMemo(() => {
        return getSortedData(orderBy, order, files);
    }, [order, orderBy, files]);
    const handlePopoverOpen = (event, id) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setOpenPopoverId(id);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setOpenPopoverId(null);
    };

    const handleSelectAll = () => {
        dispatch(fileUploadActions.handleSelected({ type: "selected_all" }));
    };

    const handleCheckboxChange = (id) => {
        dispatch(fileUploadActions.handleSelected({ type: 'row_selected', selectedId: id }));
    };

    const handleTableRowClick = (event, id) => {
        const isCheckboxColumn = event.target.closest('.table-row-checkbox');
        const isActionsColumn = event.target.closest('.table-row-actions');
        const isPopoverOpen = Boolean(anchorEl);

        if (!isCheckboxColumn && !isActionsColumn && !isPopoverOpen) {
            handlePopoverClose();
        }
    };

    const onclose = () => {
        handlePopoverClose();
        setOpenDelete(!openDelete)
    }
    const handleDeleteOpen = () => {
        setOpenDelete(true)
    }

    const handleDelete = () => {
        handlePopoverClose();
        if (openPopoverId) {
            dispatch(deleteFiles({ companyId: company.id, data: { ids: [openPopoverId] } })).then(result => {
                if (result.payload.status_code === 200) {
                    dispatch(fileUploadActions.setSelectedAllFalse());
                    dispatch(fileUploadActions.handleSelected({ type: 'unselect' }));
                    dispatch(getFiles({ companyId: company.id }))
                    onclose();
                }
            });
        }
    }

    const handlePageChange = (event, page) => {
        dispatch(fileUploadActions.setCurrentPage(page));
    };

    const handleNextClick = () => {
        if (currentPage < totalPages) {
            handlePageChange(_, currentPage + 1);
        }
    };

    const handlePreviousClick = () => {
        if (currentPage > 1) {
            handlePageChange(_, currentPage - 1);
        }
    }
    const renderNoData = () => (
        <TableRow>
            <TableCell colSpan={5}>
                <Box textAlign="center" p={3}>
                    <Typography className="text-sm-regular gray-900">
                        No files to download.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );

    const renderTable = () => {
        const startIdx = (currentPage - 1) * pageSize;
        const endIdx = currentPage * pageSize;
        return sortedData?.length > 0 ? sortedData?.slice(startIdx, endIdx).map((row, index) => {
            const handleDownloadClick = () => {
                downloadFiles([{file_url:row.file_url, file_name: row.file_name}]);
                dispatch(fileUploadActions.handleSelected({ type: 'unselect' }));
            };

            return (
                <TableRow key={row?.id} className='cursor-pointer' onClick={(event) => handleTableRowClick(event, row.id)}>
                    <TableCell className='table-row-data no-border table-row-checkbox'>
                        <Checkbox
                            checked={selectedFiles.includes(row?.id)}
                            onChange={() => handleCheckboxChange(row.id)}
                        />
                    </TableCell>
                    <TableCell className='table-row-data no-border'>
                        {row?.file_name}
                    </TableCell>
                    <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                        {row?.file_type}
                    </TableCell>
                    <TableCell className='table-row-data no-border font-weight-500 gray-900'>
                        {row?.created_at.split(' ')[0]}
                    </TableCell>
                    <TableCell className='table-row-data no-border cursor-pointer table-row-actions'>
                        <img src={ActionsIcon} height={20} width={20} onClick={(event) => handlePopoverOpen(event, row.id)} />
                        <Popover
                            open={openPopoverId === row.id}
                            anchorEl={anchorEl}
                            onClose={handlePopoverClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            disableScrollLock={true}
                            sx={{
                                '& .MuiPaper-root': {
                                    boxShadow:
                                        '0px 4px 6px -2px rgba(16, 24, 40, 0.05), inset 0px 8px 10px -7px rgba(16, 24, 40, 0.10)',
                                },
                            }}
                        >
                            <Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={handleDownloadClick}
                                >
                                    <DownArrow color='#667085' rectcolor="white" />
                                    <Typography className="action-button-text">
                                        Download
                                    </Typography>
                                </Box>
                                <Box
                                    className="action-button-box table-row-actions"
                                    onClick={handleDeleteOpen}
                                >
                                    <Trash color='#667085' />
                                    <Typography className="action-button-text">
                                        Delete
                                    </Typography>
                                </Box>
                            </Box>
                        </Popover>
                    </TableCell>
                </TableRow>
            );
        }) : renderNoData();
    };

    return (
        <>
            {/* Sources History */}
            <Grid container spacing={'32px'}>
                {/* Sources History Table */}
                <Grid item xs={12}>
                    <Box className="b1px-border-box-br8px">
                        {/* Table */}
                        <Box>
                            <TableContainer component={Paper} className='no-box-shadow no-border'>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell className="queries-headers-title" data-testid="data_source_table_selectAll">
                                                <Checkbox
                                                    checked={isSelectedAll}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                            {Files_column.map((column, index) => (
                                                <TableCell
                                                    key={index}
                                                    align='left'
                                                    className='text-500fw-12fs-18lh bg-gray-50'
                                                >
                                                    {column.key !== "options" &&
                                                        <TableSortLabel
                                                            active={orderBy === column.key}
                                                            direction={orderBy === column.key ? order : 'asc'}
                                                            onClick={createSortHandler(column.key)}
                                                        >
                                                            {column.value}
                                                        </TableSortLabel>}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {isGetFilesLoading ? (
                                            <TableRowsSkeleton
                                                rows={10}
                                                columns={5}
                                            />
                                        ) : (
                                            renderTable()
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                        {/* Table Footer */}
                        {totalPages > 1 ? (
                            <Box className="padding-tb-16-rl-24 border-top-gray">
                                <Box className="flex-row-space-between-center">
                                    <Box>
                                        <CustomButton
                                            title="Previous"
                                            titleColor="#344054"
                                            buttonColor="#FFFFFF"
                                            icon={<ArrowLeftIcon />}
                                            customStyle={{ borderRadius: '8px' }}
                                            handleClick={handlePreviousClick}
                                            buttonDisabled={!(currentPage !== 1)}
                                        />
                                    </Box>

                                    {/* Pagination */}
                                    <Box>
                                        <Pagination
                                            shape="rounded"
                                            count={totalPages}
                                            page={currentPage}
                                            hidePrevButton
                                            hideNextButton
                                            onChange={handlePageChange}
                                        />
                                    </Box>

                                    <Box>
                                        <CustomButton
                                            title="Next"
                                            titleColor="#344054"
                                            buttonColor="#FFFFFF"
                                            tailIcon={<ArrowRightIcon />}
                                            customStyle={{ borderRadius: '8px' }}
                                            handleClick={handleNextClick}
                                            buttonDisabled={!(totalPages > currentPage)}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        ) : null}
                    </Box>
                </Grid>
            </Grid>
            <ConfirmationDelete
                open={openDelete}
                onClosed={onclose}
                onConfirms={handleDelete} />
        </>
    );
};

export default FilesTable;
